import camelCase from "lodash/camelCase";
import useFields from "../../../../hooks/useFields";
import useConfigurableFields from "../../../../hooks/configurable-fields";
import useEnabledCustomFieldIds from "../../../../hooks/capture/useEnabledCustomFieldIds";
import { getMissingBillingFields } from "../../../../utils/billingFieldsHelpers";

export const exclude = [
  "item_id",
  "barcode",
  "quantity_ordered",
  "available_quantity",
  "rack_locations",
  "destination_room_number",
  "attachments",
  "receiving_report_number",
  "outbound_summary",
  "minimum_quantity",
  "warehouse_quantity",
  "allocated_quantity",
  "last_in",
  "last_out",
  "in_cart_quantity",
  "reserved_quantity",
  "unit_cost",
  "created_at",
  "updated_at",
  "osd_notes",
  "general_notes",
  "id_cost_center",
  "days_left_to_expire",
  "warehouse_name",
  "filter_field2_name",
  "billing_type",
  "asset_label_pdf",
];

export const useItemSchema = (display, project, asset, shipment) => {
  const excludeFields = [...exclude, "barcode_number", "photo"];

  const fields = useFields();
  const { customFields = [] } = useConfigurableFields();
  const enabledCustomFields = useEnabledCustomFieldIds(customFields, display);

  const validationList = fields
    .filter(v => v.enabled)
    .flatMap(v => v.list)
    .map(([key]) => key)
    .filter(key => display[key])
    .filter(key => !excludeFields.includes(key))
    .map(v => [v, camelCase(v)]);
  const requiredFieldNames = validationList.map(([_, camelKey]) => camelKey);

  const customFieldRules = enabledCustomFields.map(field => ({
    key: `field_${field.id}`,
    type: field.fieldType,
  }));

  const isEmpty = value => {
    return (
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0)
    );
  };

  return values => {
    const errors = {};

    requiredFieldNames.forEach(fieldName => {
      if (isEmpty(values[fieldName])) {
        errors[fieldName] = "Required";
      }
    });

    if (display.barcode_number && isEmpty(values.barcode)) {
      errors.barcode = "Required";
    }

    if (project.shipmentItemUnitBreakdown) {
      const needsTotalPieces = !!values.description && Number(values.shipmentItemQuantity) > 0;
      if (needsTotalPieces && isEmpty(values.totalPieces)) {
        errors.totalPieces = "Required";
      }
    }

    // Always validate shipmentItemQuantity regardless of available_quantity setting
    const qty = Number(values.shipmentItemQuantity);
    if (isEmpty(values.shipmentItemQuantity)) {
      errors.shipmentItemQuantity = "Required";
    } else if (!Number.isFinite(qty) || qty <= 0) {
      errors.shipmentItemQuantity = "Must be greater than 0";
    }

    if (customFieldRules.length > 0) {
      const customErrors = {};
      customFieldRules.forEach(rule => {
        const value = values.customFields ? values.customFields[rule.key] : undefined;
        switch (rule.type) {
          case "decimal": {
            if (isEmpty(value)) {
              customErrors[rule.key] = "Required";
            } else if (!Number.isFinite(Number(value))) {
              customErrors[rule.key] = "Must be a valid number";
            }
            break;
          }
          case "boolean":
            if (value === undefined) {
              customErrors[rule.key] = "Required";
            }
            break;
          case "string":
          default: {
            if (isEmpty(value)) {
              customErrors[rule.key] = "Required";
            }
          }
        }
      });
      if (Object.keys(customErrors).length > 0) {
        errors.customFields = customErrors;
      }
    }

    const minimumImages = asset?.costCenter?.minimumAssetImages || project.minimumAssetImages || 0;
    if (minimumImages > 0) {
      const imageCount = asset?.images?.length || 0;
      if (imageCount < minimumImages) {
        errors.photo = minimumImages === 1 ? "Minimum 1 image required" : `Minimum ${minimumImages} images required`;
      }
    }

    const missingBillingFields = getMissingBillingFields(project, shipment);
    if (missingBillingFields.length > 0) {
      missingBillingFields.forEach(fieldName => {
        const camelFieldName = camelCase(fieldName);
        if (isEmpty(values[camelFieldName])) {
          errors[camelFieldName] = "Required";
        }
      });
    }

    return errors;
  };
};
